import { DataResult, process, State } from "@progress/kendo-data-query";
import { Icon } from "@progress/kendo-react-common";
import { Dialog } from "@progress/kendo-react-dialogs";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
} from "@progress/kendo-react-grid";
import { useInternationalization } from "@progress/kendo-react-intl";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import accumulatorApi from "../../api/accumulator";
import companyApi from "../../api/company";
import productsApi from "../../api/products";
import quotesApi from "../../api/quotes";
import usersApi from "../../api/users";
import { useApi, useFetch } from "../../hooks/useApi";
import { UserCompany } from "../../hooks/useCompanies";
import { useUserContext } from "../../hooks/useUserContext";
import { getIconPath } from "../../utilities/textUtilities";
import { CustomGridCell, DateGridCell, EditLinkCell, NumberGridCell } from "../grid";
import { CopyPopup, ICommodity, IInstrument, Instrument } from "../quotes";
import { QuoteDetailsSummary } from "../quotes/quoteDetails";
import { QuoteActionsCell } from "./QuoteActionsCell";
import { StatusCell } from "./StatusCell";

interface QuoteIdProps {
  cellProps: GridCellProps;
  onClick: (showDialog: number) => void;
}

const QuoteIdCell = (props: QuoteIdProps) => {
  const { cellProps, onClick } = props;
  const { field, dataItem } = cellProps;
  const value = parseInt(dataItem[field || ""]) || 0;

  return (
    <EditLinkCell {...cellProps} title="View Quote Details" enterEdit={() => onClick(value)} />
  );
};

const QuotedByCell = (props: GridCellProps) => {
  const { field, dataItem } = props;
  const companyName: string = dataItem[field || ""];
  const quotedBy: string = dataItem.quotedBy || "";

  return (
    <CustomGridCell {...props}>
      {quotedBy && <div>{quotedBy}</div>}
      <div style={{ fontSize: "smaller" }}>{companyName}</div>
    </CustomGridCell>
  );
};

const ProductCell = (props: GridCellProps) => {
  const { field, dataItem } = props;
  const instrument: string = dataItem[field || ""];
  const instrumentType = dataItem.productType || "";
  const commodityTitle = dataItem.commodityName || "";
  const { tickerCode: commodity, commodityType, settlementType } = dataItem;

  return (
    <CustomGridCell {...props}>
      <img
        alt={commodityTitle}
        src={getIconPath("commodity", commodityType)}
        title={commodityTitle}
        style={{ width: 16, height: 16, marginRight: ".5rem" }}
      />
      <span title={commodityTitle}>{commodity}</span>
      <br />
      <img
        alt={instrumentType}
        src={getIconPath("instrument", instrumentType)}
        title={instrumentType}
        style={{ width: 16, height: 16, marginRight: ".5rem" }}
      />
      <span>{settlementType?.length > 0 ? `${settlementType} ${instrument}` : instrument}</span>
    </CustomGridCell>
  );
};

interface PriceCellProps {
  cellProps: GridCellProps;
  commodities: ICommodity[];
  isRiskTaker?: boolean;
}

const PriceCell = ({ cellProps, commodities, isRiskTaker }: PriceCellProps) => {
  const { dataItem } = cellProps;
  const maxVolume: number = dataItem.maxVolume || dataItem.volume || 1;
  const commodity = commodities?.find((i: ICommodity) => i.id === dataItem.commodityId);
  const decimals = commodity?.decimals ?? 3;
  const formatter = useInternationalization();
  const rawValue = dataItem.brokerPrice / maxVolume;
  const value = !isRiskTaker && rawValue < 0 ? 0 : rawValue;

  return (
    <CustomGridCell {...cellProps}>
      <span>{formatter.formatNumber(value, `n${decimals}`)}</span>
    </CustomGridCell>
  );
};

const KnockOutLevelCell = ({ cellProps, commodities }: PriceCellProps) => {
  const { dataItem } = cellProps;
  const { knockOutLevel, commodityId } = dataItem;
  const commodity = commodities?.find((i: ICommodity) => i.id === commodityId);
  const decimals = commodity?.decimals ?? 3;
  const formatter = useInternationalization();

  return (
    <CustomGridCell {...cellProps}>
      <span>{formatter.formatNumber(knockOutLevel, `n${decimals}`)}</span>
    </CustomGridCell>
  );
};

const StrikeCell = ({ cellProps, commodities }: PriceCellProps) => {
  const { dataItem } = cellProps;
  const { strike, commodityId } = dataItem;
  const commodity = commodities?.find((i: ICommodity) => i.id === commodityId);
  const decimals = commodity?.decimals ?? 3;
  const formatter = useInternationalization();

  return (
    <CustomGridCell {...cellProps}>
      <span>{formatter.formatNumber(strike, `n${decimals}`)}</span>
    </CustomGridCell>
  );
};

interface QuotesGridProps {
  quotes: any[];
  commodities: ICommodity[];
  onDataChange: (result: DataResult | undefined, state?: State) => void;
  onRefresh: () => void;
}

export const AccumulatorQuotesGrid = (props: QuotesGridProps) => {
  const { quotes, commodities, onDataChange, onRefresh } = props;
  const { context: user } = useUserContext();
  const { permissions, roles } = user;
  const navigate = useNavigate();
  const copyQuoteApi = useApi(quotesApi.copyQuote);
  const deleteQuoteApi = useApi(quotesApi.deleteQuote);
  const accumulatorQuoteApi = useApi(accumulatorApi.getQuote);
  const getInstrumentsApi = useApi(productsApi.getInstruments);
  const companiesApi = useFetch(companyApi.getLineage);
  const profile = useFetch(usersApi.get);

  const [result, setResult] = useState<DataResult>();
  const [dataState, setDataState] = useState<State>({
    sort: [],
    skip: 0,
    take: 20,
  });
  const [item, setItem] = useState<any | null>(null);
  const [copyCompany, setCopyCompany] = useState<any | null>(null);
  const [action, setAction] = useState("");
  const [showDetailsId, setShowDetailsId] = useState(0);
  const [useStreamlined, setUseStreamlined] = useState(false);

  useEffect(() => {
    var userStreamlined =
      (profile.data?.user?.preferences || []).includes(1) && //TODO: Enum
      (user.roles.isRiskTaker || user.roles.isBroker);
    setUseStreamlined(userStreamlined);
  }, [profile.data]);

  useEffect(() => {
    applyDataStateChange(dataState);
  }, [quotes]);

  const createDataState = (dataState: State) => {
    return {
      result: process(quotes.slice(0), dataState),
      dataState: dataState,
    };
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    applyDataStateChange(event.dataState);
  };

  const applyDataStateChange = (dataState: State) => {
    const updatedState = createDataState(dataState);
    setResult(updatedState.result);
    setDataState(updatedState.dataState);

    onDataChange && onDataChange(updatedState.result, updatedState.dataState);
  };

  const copyQuote = async (company: any | null) => {
    const id = company?.id ?? quoteToCopy.companyId;
    const copyCompany = companiesApi.data?.companies.find((c: any) => c.id === id);

    setItem(quoteToCopy);
    setCopyCompany(copyCompany);
    await Promise.all([
      getInstrumentsApi.request(quoteToCopy.commodityId),
      copyQuoteApi.request(quoteToCopy.quoteId, copyCompany.id),
    ]);
  };

  useEffect(() => {
    if (!copyQuoteApi.loading && copyQuoteApi.data && item && getInstrumentsApi.data) {
      setAction("");
      if (!copyQuoteApi.data.quoteId) return; //TODO: Handle error

      const inst = getInstrumentsApi.data.instruments.find(
        (i: IInstrument) => i.id == item.instrumentId,
      );
      const quoteInstrument = Instrument.create(inst);
      const quoteType = quoteInstrument.quoteType();
      const useDealCapture = useStreamlined && !quoteInstrument.isAccumulator();

      navigate(useDealCapture ? "/quote/dealcapture" : `/quote/${quoteType}/0`, {
        state: {
          data: {
            company: UserCompany.create(copyCompany),
            commodity: commodities?.find((i: ICommodity) => i.id == item.commodityId),
            instrument: inst,
            quoteId: copyQuoteApi.data.quoteId,
          },
        },
      });
    }
  }, [copyQuoteApi.data, copyQuoteApi.error, getInstrumentsApi.data]);

  const editQuote = async (dataItem: any) => {
    setItem(dataItem);
    await getInstrumentsApi.request(dataItem.commodityId);
  };

  useEffect(() => {
    if (getInstrumentsApi.data && action === "edit") {
      const inst = getInstrumentsApi.data.instruments.find(
        (i: IInstrument) => i.id === item.instrumentId,
      );
      const quoteInstrument = Instrument.create(inst);
      const quoteType = quoteInstrument.quoteType();
      const useDealCapture = useStreamlined && !quoteInstrument.isAccumulator();

      navigate(useDealCapture ? "/quote/dealcapture" : `/quote/${quoteType}/0`, {
        state: {
          data: {
            company: UserCompany.create(
              companiesApi.data?.companies.find((c: any) => c.id === item.companyId),
            ),
            commodity: commodities?.find((i: ICommodity) => i.id == item.commodityId),
            instrument: inst,
            quoteId: item.quoteId,
          },
        },
      });
    }
  }, [getInstrumentsApi.data]);

  const deleteQuote = async (quoteId: number) => {
    await deleteQuoteApi.request(quoteId);
    //TODO: error handling

    setAction("");
    onRefresh();
  };

  const requote = async (quoteId: number, productType: string) => {
    await accumulatorQuoteApi.request(quoteId);
    //TODO: error handling

    setAction("");
    onRefresh();
  };

  const [quoteToCopy, setQuoteToCopy] = useState<any | null>(null);
  const handleOpenCopyDialog = (newQuoteToCopy: any) => {
    setQuoteToCopy(newQuoteToCopy);
  };

  const PriceCellWrapper = (props: GridCellProps) => (
    <PriceCell cellProps={props} commodities={commodities} isRiskTaker={user.roles.isRiskTaker} />
  );

  const KnockOutLevelCellWrapper = (props: GridCellProps) => (
    <KnockOutLevelCell cellProps={props} commodities={commodities} />
  );

  const StrikeCellWrapper = (props: GridCellProps) => (
    <StrikeCell cellProps={props} commodities={commodities} />
  );

  const QuoteIdCellWrapper = (props: GridCellProps) => (
    <QuoteIdCell cellProps={props} onClick={setShowDetailsId} />
  );

  const QuoteActionsCellWrapper = (props: GridCellProps) => {
    return (
      <QuoteActionsCell
        cellProps={props}
        action={action}
        permissions={permissions}
        roles={roles}
        onActionClick={(newAction: string) => setAction(newAction)}
        onCopy={handleOpenCopyDialog}
        onEdit={editQuote}
        onDelete={deleteQuote}
        onQuote={requote}
      />
    );
  };

  const closeDetailsDialog = () => {
    setShowDetailsId(0);
    onRefresh();
  };

  const [width, setWidth] = useState(window.innerWidth);
  window.addEventListener("resize", (e: any) => setWidth(e.target.innerWidth));

  const setPercentage = (percentage: number) => {
    return Math.round((width * 0.95) / 100) * percentage;
  };

  return (
    <>
      <div>
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <Grid
            data={result}
            {...dataState}
            pageable={{ pageSizes: [10, 20, 50] }}
            sortable={true}
            resizable={true}
            onDataStateChange={dataStateChange}
          >
            <GridColumn
              field="quoteId"
              title="Id"
              cells={{ data: QuoteIdCellWrapper }}
              width={setPercentage(5)}
            />

            {!roles.isClient && (
              <GridColumn
                field="companyName"
                title="Quoted By"
                cells={{ data: QuotedByCell }}
                width={setPercentage(10)}
              />
            )}

            <GridColumn
              field="quoteName"
              title="Quote Name"
              width={setPercentage(roles.isClient ? 20 : 15)}
            />

            <GridColumn
              field="structure"
              title="Product"
              cells={{ data: ProductCell }}
              width={setPercentage(12)}
            />

            <GridColumn
              field="quoteDate"
              title="Quoted"
              cells={{ data: DateGridCell }}
              width={setPercentage(8)}
            />

            <GridColumn
              field="volume"
              title="Volume"
              cells={{ data: NumberGridCell }}
              format="n0"
              width={setPercentage(6)}
            />

            <GridColumn
              field="knockOutLevel"
              title="Knock-Out Level"
              cells={{ data: KnockOutLevelCellWrapper }}
              width={setPercentage(8)}
            />

            <GridColumn
              field="strike"
              title="Strike"
              cells={{ data: StrikeCellWrapper }}
              width={setPercentage(6)}
            />

            <GridColumn
              field="price"
              title="Price ($/unit)"
              cells={{ data: PriceCellWrapper }}
              width={setPercentage(6)}
            />

            <GridColumn
              field="status"
              title="Status"
              cells={{ data: StatusCell }}
              width={setPercentage(5)}
            />
            <GridColumn key="actions" cells={{ data: QuoteActionsCellWrapper }} />
          </Grid>
        </Tooltip>
      </div>

      {showDetailsId > 0 && (
        <Dialog width={"80%"} height="80%" closeIcon={true}>
          <div className="float-right">
            <Icon name="x" style={{ cursor: "pointer" }} onClick={closeDetailsDialog} />
          </div>
          <QuoteDetailsSummary quoteId={showDetailsId} onReject={closeDetailsDialog} />
        </Dialog>
      )}

      {!!quoteToCopy && (
        <CopyPopup
          data={{
            ...quoteToCopy,
          }}
          handleClose={() => setQuoteToCopy(null)}
          onConfirm={(company: any | null) => copyQuote(company)}
        />
      )}
    </>
  );
};
